import type { BetClassification, PrimaryLabelKey } from "../types/bets";

/**
 * Presentation-only mapping from the backend's `classification.primary_label_key`
 * to a human-readable bet type label.
 *
 * Classification rules live in the backend (`trivelta_helper`); the frontend
 * must not re-derive subtype from raw fields (`flex.version`, `sgp_id`, etc.).
 * This map is the single place to adjust wording / i18n for the bets table.
 * Keyed by the full `PrimaryLabelKey` union so a new backend value fails the
 * type-check until it is given a label here.
 */
export const BET_TYPE_LABELS: Record<PrimaryLabelKey, string> = {
  single: "Single",
  p2p: "P2P",
  casino: "Casino",
  round_robin: "Round Robin",
  group_singles: "Group Bet",
  parlay: "Parlay",
  sgp: "SGP",
  flex_v1: "Flex (v1)",
  flex_v2: "Flex",
  sgp_flex_v1: "SGP · Flex (v1)",
  sgp_flex_v2: "SGP · Flex",
  teaser: "Teaser",
  dst_parlay: "DST Parlay",
  oddin_parlay: "Oddin Parlay",
  pikkem_super: "Super Pick",
  pikkem_flex: "Pikkem Flex",
  unknown: "Bet",
};

/**
 * Maps a backend classification block to a display label.
 * - No classification / no key => "" (blank cell, e.g. legacy items).
 * - Unknown key => "Bet" (safe fallback for a label the UI doesn't know yet).
 */
export const betTypeLabel = (
  classification: BetClassification | null | undefined,
): string => {
  const key = classification?.primary_label_key;
  if (!key) return "";
  return BET_TYPE_LABELS[key] ?? "Bet";
};
